import { User } from "@/core/models/global/User";
import { PersonalInfoFormData } from "@/core/models/profile/PersonalInfoFormData";
import { AddressFormData, HomeAddress } from "@/core/models/profile/AddressFormData";
//import { StudentDetailsFormData } from "@/core/models/profile/StudentDetailsFormData";
import { SecondaryEducationFormData } from "@/core/models/profile/SecondaryEducationFormData";
import { PostSecondaryEducationFormData } from "@/core/models/profile/PostSecondaryEducationFormData";
import { MilitaryInfoFormData } from "@/core/models/profile/MilitaryInfoFormData";
import { EmploymentInfoFormData } from "@/core/models/profile/EmploymentInfoFormData";
import { PAGE_IDS } from '@/core/constants/pageIds';

function pick(u: any, ...names: string[]) {
    for (const n of names) {
        if (u == null) break;
        
        // Try exact match first
        if (Object.prototype.hasOwnProperty.call(u, n) && u[n] !== undefined && u[n] !== null) {
            return u[n];
        }
        
        // Try case-insensitive match
        const lowerN = n.toLowerCase();
        for (const key of Object.keys(u)) {
            if (key.toLowerCase() === lowerN && u[key] !== undefined && u[key] !== null) {
                return u[key];
            }
        }
    }
    return undefined;
}

function toYesNoString(value: unknown): string | undefined {
    if (value === undefined || value === null || value === "") return undefined;

    if (typeof value === "boolean") return value ? "Yes" : "No";

    if (typeof value === "number") {
        if (value === 1) return "Yes";
        if (value === 0) return "No";
        return String(value);
    }

    const normalized = String(value).trim().toLowerCase();

    if (["yes", "y", "true", "1"].includes(normalized)) return "Yes";
    if (["no", "n", "false", "0"].includes(normalized)) return "No";

    return String(value);
}

function toDateValue(value: unknown): Date | undefined {
    if (!value) return undefined;
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? undefined : value;
    }

    const dt = new Date(String(value));
    return isNaN(dt.getTime()) ? undefined : dt;
}

export interface PageData
{
    applicantId?: number,
    pageId?: string
}

export interface MappedUser
{
    personal: PersonalInfoFormData,
    addressInfo: AddressFormData,
    //studentDetails: StudentDetailsFormData, 
    secondaryEducation: SecondaryEducationFormData,
    postSecondaryEducation: PostSecondaryEducationFormData,
    militaryInfo: MilitaryInfoFormData,
    employmentInfo: EmploymentInfoFormData,
    completedPages: Set<string>,
}

export function mapUserToForms(user: User | null): MappedUser {
    const applicantId = Number(pick(user as any, 'applicantId', 'ApplicantId')) || 0;


    const personal = new PersonalInfoFormData(applicantId, PAGE_IDS.personal);
    const addressInfo = new AddressFormData(applicantId, PAGE_IDS.addressInfo);
    const secondaryEducation = new SecondaryEducationFormData(applicantId, PAGE_IDS.secondaryEducation);
    const postSecondaryEducation = new PostSecondaryEducationFormData(applicantId, PAGE_IDS.postSecondaryEducation);
    const militaryInfo = new MilitaryInfoFormData(applicantId, PAGE_IDS.militaryInfo);
    const employmentInfo = new EmploymentInfoFormData(applicantId, PAGE_IDS.employmentInfo);
    const completedPages: Set<string> = new Set<string>();

    if (!user)
    {
        return {
            personal,
            addressInfo,
            secondaryEducation,
            postSecondaryEducation,
            militaryInfo,
            employmentInfo,
            completedPages,
        };
    }

    const u: any = user;

    //Personal
    personal.FirstName = String(pick(u, 'firstName', 'FirstName', 'firstname') ?? personal.FirstName ?? '');
    personal.LastName = String(pick(u, 'lastName', 'LastName', 'lastname') ?? personal.LastName ?? '');
    personal.MiddleName = String(pick(u, 'middleName', 'MiddleName', 'Middlename') ?? personal.MiddleName ?? '');
    // Maiden name may come from ExtraApplicantInfo merged fields
    personal.MaidenName = String(pick(u, 'MaidenName', 'maidenName', 'Maiden') ?? personal.MaidenName ?? '');
       
    // Phone aliases
    personal.Phone = String(pick(u, 'phone', 'Phone', 'PhoneNumber', 'phoneNumber') ?? personal.Phone ?? '');
    personal.PhoneConfirm = String(pick(u, 'mobileNumber', 'MobileNumber') ?? personal.PhoneConfirm ?? personal.Phone ?? '');

    // Date of birth: accept multiple shapes
    const dobRaw = pick(u, 'dateOfBirth', 'DateOfBirth', 'DOB', 'dob');
    if (dobRaw) {
        if (dobRaw instanceof Date) {
            personal.DateOfBirth = dobRaw.toISOString().slice(0, 10);
            personal.DOB = personal.DateOfBirth;
        } else {
            const s = String(dobRaw);
            // if already ISO date, keep; otherwise try to parse
            try {
                const dt = new Date(s);
                if (!isNaN(dt.getTime())) personal.DateOfBirth = dt.toISOString().slice(0, 10);
                else personal.DateOfBirth = s;
            } catch {
                personal.DateOfBirth = s;
            }
            personal.DOB = personal.DateOfBirth;
        }
    }

    // Gender / sex
    const g = pick(u, 'gender', 'Gender', 'AmSexID', 'AmSexId');
    if (g !== undefined && g !== null) {
        personal.Gender = String(g);
        personal.AmSexId = String(g);
    }

    // Marital
    const m = pick(u, 'maritalStatus', 'MaritalStatus', 'AmMaritalID', 'AmMaritalId');
    if (m !== undefined && m !== null) {
        personal.MaritalStatus = String(m);
        personal.AmMaritalID = String(m);
    }

    // Hispanic / Latino
    const h = pick(u, 'hispanicOrLatino', 'HispanicOrLatino', 'Hispanic', 'IsHispanic');
    if (h !== undefined && h !== null) {
        personal.HispanicOrLatino = String(h);
    }

    // Race
    const r = pick(u, 'raceDescription', 'RaceDescription', 'Race');
    if (r !== undefined && r !== null) {
        personal.RaceDescription = String(r);
    }

    // Citizenship
    const c = pick(u, 'amCitizenID', 'AmCitizenID', 'CitizenStatus', 'citizenshipStatus');
    if (c !== undefined && c !== null) personal.AmCitizenID = String(c);

    if ((!personal.FirstName || !personal.LastName) && !personal.FirstName && !personal.LastName) {
        const full = pick(u, 'name_', 'Name', 'fullName', 'displayName') as string | undefined;
        if (full) {
            const parts = String(full).trim().split(/\s+/);
            personal.FirstName = personal.FirstName || parts[0] || '';
            personal.LastName = personal.LastName || parts.slice(1).join(' ') || '';
        }
    }

    //Address
    const ha = new HomeAddress();
    ha.Addr1 = String(pick(u, 'Addr1', 'addr1', 'address', 'Address') ?? '');
    ha.Addr2 = String(pick(u, 'Addr2', 'addr2', 'Address2', 'address2', 'addressLine2', 'AddressLine2') ?? '');
    ha.City = String(pick(u, 'City', 'city') ?? '');
    ha.State = pick(u, 'State', 'state') ?? undefined;
    ha.Zip = String(pick(u, 'Zip', 'zip', 'postalCode') ?? '');
    ha.SyCountryID = pick(u, 'SyCountryID', 'countryId', 'Country') ?? undefined;
    addressInfo.HomeAddress = ha;

    // Military
    const toOptionalString = (value: unknown): string | undefined => {
        if (value === undefined || value === null || value === "") return undefined;
        return String(value);
    };

    militaryInfo.MilitaryStatus = toYesNoString(
        pick(u, 'MilitaryStatus', 'militaryStatus', 'IsActiveDutyRetired', 'isActiveDutyRetired')
    );

    militaryInfo.UsingGiBill = toYesNoString(
        pick(u, 'UsingGiBill', 'usingGiBill', 'IsUsingGIBill', 'isUsingGIBill')
    );

    militaryInfo.TransferredBenefit = toYesNoString(
        pick(u, 'TransferredBenefit', 'transferredBenefit')
    );

    militaryInfo.TuitionAssistanceExpected = toYesNoString(
        pick(u, 'TuitionAssistanceExpected', 'tuitionAssistanceExpected')
    );

    militaryInfo.GiBillBenefits = toOptionalString(
        pick(u, 'GiBillBenefits', 'giBillBenefits')
    );

    militaryInfo.TransferorName = toOptionalString(
        pick(u, 'TransferorName', 'transferorName')
    );

    militaryInfo.CurrentMilitaryStatus = toOptionalString(
        pick(u, 'CurrentMilitaryStatus', 'currentMilitaryStatus')
    );

    militaryInfo.MilitaryBranch = toOptionalString(
        pick(u, 'MilitaryBranch', 'militaryBranch')
    );

    militaryInfo.MilitaryRank = toOptionalString(
        pick(u, 'MilitaryRank', 'militaryRank')
    );

    militaryInfo.MilitaryInstallation = toOptionalString(
        pick(u, 'MilitaryInstallation', 'militaryInstallation')
    );

    militaryInfo.YearsOfService = toOptionalString(
        pick(u, 'YearsOfService', 'yearsOfService')
    );

    militaryInfo.MilitaryRateMOS = toOptionalString(
        pick(u, 'MilitaryRateMOS', 'militaryRateMOS', 'MilitaryMos', 'militaryMos')
    );

    const militaryTranscriptRequested = pick(
        u,
        'MilitaryTranscriptRequested',
        'militaryTranscriptRequested'
    );

    militaryInfo.MilitaryTranscriptRequested =
        toYesNoString(militaryTranscriptRequested) ??
        (militaryTranscriptRequested !== undefined && militaryTranscriptRequested !== null
            ? String(militaryTranscriptRequested)
            : undefined);

    militaryInfo.CH33ExpectedPercentage = toOptionalString(
        pick(u, 'CH33ExpectedPercentage', 'ch33ExpectedPercentage')
    );

    militaryInfo.IntendToUseMyCAA = toOptionalString(
        pick(u, 'IntendToUseMyCAA', 'intendToUseMyCAA')
    );
    militaryInfo.HasMilitarySpouse = toOptionalString(pick(u, 'HasMilitarySpouse', 'hasMilitarySpouse'));
    militaryInfo.SpouseMilitaryStatus = toOptionalString(pick(u, 'SpouseMilitaryStatus', 'spouseMilitaryStatus'));
    militaryInfo.HasMilitaryParent = toOptionalString(pick(u, 'HasMilitaryParent', 'hasMilitaryParent'));
    militaryInfo.ParentMilitaryStatus = toOptionalString(pick(u, 'ParentMilitaryStatus', 'parentMilitaryStatus'));

    militaryInfo.MilitaryTranscriptDateRequested = toDateValue(
        pick(u, 'MilitaryTranscriptDateRequested', 'militaryTranscriptDateRequested')
    );
      
    // Prefer the raw JSON string forwarded by the server (CollegesRawJson) so we
    // get all institution fields. Fall back to the pre-parsed CollegesParsed list.
    const collegesRaw = pick(u, 'collegesRawJson', 'CollegesRawJson');
    let collegesArray: any[] | null = null;
    let collegesHasAttended: boolean | undefined;

    if (typeof collegesRaw === 'string' && collegesRaw.trim()) {
        try {
            const parsed = JSON.parse(collegesRaw);
            if (Array.isArray(parsed)) {
                collegesArray = parsed;
            } else if (parsed && typeof parsed === 'object') {
                if (typeof parsed.hasAttendedCollege === 'boolean') collegesHasAttended = parsed.hasAttendedCollege;
                if (typeof parsed.HasAttendedCollege === 'boolean') collegesHasAttended = parsed.HasAttendedCollege;
                const inst = parsed.institutions ?? parsed.Institutions;
                if (Array.isArray(inst)) collegesArray = inst;
            }
        } catch { /* fall through to CollegesParsed */ }
    }

    if (!collegesArray) {
        const fallback = pick(u, 'collegesParsed', 'CollegesParsed');
        if (Array.isArray(fallback)) collegesArray = fallback;
    }

    if (Array.isArray(collegesArray) && collegesArray.length > 0) {
        postSecondaryEducation.Institutions = collegesArray.map((c: any) => ({
            ApplicantPostSecondaryId: c.applicantPostSecondaryId ?? c.ApplicantPostSecondaryId ?? undefined,
            ApplicantCollegeId: c.applicantCollegeId ?? c.ApplicantCollegeId ?? undefined,
            Id: c.id ?? c.Id ?? undefined,
            AmCollegeId: c.amCollegeId ?? c.AmCollegeId ?? undefined,
            Name: c.name ?? c.Name ?? c.nameWhileEnrolled ?? c.NameWhileEnrolled ?? undefined,
            DidGraduate: c.didGraduate ?? c.DidGraduate ?? undefined,
            DegreeId: c.degreeId ?? c.DegreeId ?? undefined,
            AttendedFrom: (c.attendedFrom ?? c.AttendedFrom) ? new Date(c.attendedFrom ?? c.AttendedFrom) : undefined,
            AttendedTo: (c.attendedTo ?? c.AttendedTo) ? new Date(c.attendedTo ?? c.AttendedTo) : undefined,
            EarnedCredits: c.earnedCredits ?? c.EarnedCredits ?? undefined,
            // Accept both the client JSON-post key (uploadUnofficialTranscript) and the DB column
            // name (UploadUnofficial) — usp_GetApplicantInfo builds Colleges via SELECT * ... FOR
            // JSON PATH, so it emits the raw column "UploadUnofficial", not the transcript-suffixed key.
            UploadUnofficialTranscript:
                c.uploadUnofficialTranscript ?? c.UploadUnofficialTranscript ??
                c.uploadUnofficial ?? c.UploadUnofficial ?? undefined,
            UnofficialTranscriptFileName: c.unofficialTranscriptFileName ?? c.UnofficialTranscriptFileName ?? undefined,
            IsManualEntry: c.isManualEntry ?? c.IsManualEntry ?? undefined,
            ManualAddress: c.manualAddress ?? c.ManualAddress ?? undefined,
            ManualCity: c.manualCity ?? c.ManualCity ?? undefined,
            ManualCounty: c.manualCounty ?? c.ManualCounty ?? undefined,
            ManualState: c.manualState ?? c.ManualState ?? undefined,
            ManualZip: c.manualZip ?? c.ManualZip ?? undefined,
            Edited: false,
        }));
        postSecondaryEducation.HasAttendedCollege = collegesHasAttended ?? true;
    } else if (typeof collegesHasAttended === 'boolean') {
        postSecondaryEducation.HasAttendedCollege = collegesHasAttended;
    }
    const completed = u.completedPages ?? u.CompletedPages;

    if (Array.isArray(completed))
    {
        for (const pageData of completed)
        {
            if (!pageData)
            {
                continue;
            }

            if (typeof pageData === 'string')
            {
                completedPages.add(pageData.toLowerCase());
                continue;
            }

            if (pageData.pageId)
            {
                completedPages.add((String(pageData.pageId).toLowerCase()));
            }
        }
    }

    //if (typeof pick(u, 'extraApplicantInfoCode', 'ExtraApplicantInfoCode', 'ExtraApplicantInfo') === 'string') {
    //    miscInfo.housing = pick(u, 'extraApplicantInfoCode', 'ExtraApplicantInfoCode', 'ExtraApplicantInfo');
    //}

    return {
        personal,
        addressInfo,
        secondaryEducation,
        postSecondaryEducation,
        militaryInfo,
        employmentInfo,
        completedPages,
    };
}


const profilePageIds: string[] = Object.entries(PAGE_IDS)
    .filter(([_key, value]) => Boolean(value))
    .filter(([key]) =>
    {
        // Only the actual Personal Profile steps count toward profile completion.
        // Previously this used an exclusion list, which let Housing / Program
        // Enrollment / Financial / Interview GUIDs leak in and made the profile
        // effectively never "complete" (forcing an endless redirect to ProfilePage).
        const k = String(key).toLowerCase().replace(/\s+/g, "_");
        return [
            "personal",
            "addressinfo",
            "studentdetails",
            "secondaryeducation",
            "postsecondaryeducation",
            "militaryinfo",
            "employmentinfo",
        ].includes(k);
    })
    .map(([, guid]) => String(guid).toLowerCase());

export function isProfileComplete(user: MappedUser) : boolean
{
    // Client-side: inspect user's completedPages (from mapped forms) and compare to PAGE_IDS
    const completedPages = user.completedPages;

    // ADD THESE DEBUG LOGS:


    const allCompleted = profilePageIds.every(g => completedPages.has(g));

    const allPageIdsPresent = profilePageIds.length > 0 && allCompleted;

    return allPageIdsPresent;
}

// In local dev, MilitaryInfoForm persists/rehydrates its answers via a localStorage
// mock ("profileFormFields_dev_all" in MilitaryInfoForm.tsx) since the backend doesn't
// yet round-trip MilitaryStatus/CurrentMilitaryStatus through the user object. This
// overlays that same dev-only data on top of whatever mapUserToForms(user) produced,
// so consumers reflect what the form itself shows locally rather than the (currently
// incomplete) API data. In production this is always a no-op.
export function getEffectiveMilitaryInfo(militaryInfoFromUser: MilitaryInfoFormData): MilitaryInfoFormData {
    if (!import.meta.env.DEV) return militaryInfoFromUser;

    try {
        const raw = localStorage.getItem("profileFormFields_dev_all");
        const devMilitaryInfo = raw ? JSON.parse(raw)?.militaryInfo : undefined;
        return devMilitaryInfo ? { ...militaryInfoFromUser, ...devMilitaryInfo } : militaryInfoFromUser;
    } catch {
        return militaryInfoFromUser;
    }
}
